import React, { useRef, useState, useEffect } from 'react';
import Konva from 'konva';
import { Stage, Layer, Rect, Group, Circle, Line, Path } from 'react-konva';

import InstructionsHand from '../svg/InstructionsHand';
import { isDesktop } from '../../lib/ep';

const elements_color = '#000000';
const background_color = '#FFFFFF';

const max_width  = 240;
const max_height = max_width;

let throttle_resize;

const ScratchInstructionsCanvas = (props) => {
	const [loaded, setLoaded] = useState(false);

	let refs = useRef([]);
	let available_height = useRef(max_height);
	const stroke_length = useRef(0);

	const _container = props?.container ?? false;
	const _info_container = props?.info_container ?? false;
	const _padding_container = props?.padding_container ?? false;
	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const canvas_size_h = available_height.current;
	const scale = Math.min(1, canvas_size_h / max_height);
	const canvas_size_w = Math.round(max_width * scale);

	let hand_in_anim;
	let hand_move_anim;
	let hand_out_anim;
	let stroke_anim;

	const calculateAvailableHeight = (force_execution) => {
		if(force_execution == undefined) force_execution = false;
		if(loaded && !force_execution) return available_height.current;

		const container_el = document.querySelector(_container);
		const info_container_el = document.querySelector(_info_container);
		if(container_el == undefined || info_container_el == undefined) return max_height;

		const padding_container_el = document.querySelector(_padding_container);
		const container_styles = (padding_container_el != undefined) ? getComputedStyle(padding_container_el) : {};
		let padding = 0;
		if(container_styles.paddingTop != undefined) padding += parseInt(container_styles.paddingTop);
		if(container_styles.paddingBottom != undefined) padding += parseInt(container_styles.paddingBottom);

		const is_landscape = (!isDesktop() && window.innerWidth > window.innerHeight) ? true : false;
		(is_landscape) ? padding_container_el.classList.add('is_landscape') : padding_container_el.classList.remove('is_landscape');
		const container_height = container_el.getBoundingClientRect().height;
		const parent_height = info_container_el.getBoundingClientRect().height;

		setLoaded(true);

		available_height.current = Math.min(max_height, ((!is_landscape) ? (container_height - parent_height) : container_height) - padding);
		return ;
	};

	const killAllFrameAnimations = () => {
		if(hand_in_anim != null && hand_in_anim.isRunning()) hand_in_anim.stop();
		if(hand_move_anim != null && hand_move_anim.isRunning()) hand_move_anim.stop();
		if(hand_out_anim != null && hand_out_anim.isRunning()) hand_out_anim.stop();
		if(stroke_anim != null && stroke_anim.isRunning()) stroke_anim.stop();
	};

	const moveHandIn = () => {
		if(refs.current['hand_ref'] == undefined) return false;

		const pt = refs.current['scratch_stroke'].getPointAtLength(0);

		hand_in_anim = refs.current['hand_ref'].to({
			duration:0.6,
			x:pt.x,
			y:pt.y,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				progressStroke();
			},
		});
	};

	const moveHandOut = () => {
		if(refs.current['hand_ref'] == undefined) return false;

		hand_out_anim = refs.current['hand_ref'].to({
			duration:0.4,
			x:refs.current['hand_ref'].x() + 50,
			y:refs.current['hand_ref'].y() + 50,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				setTimeout(() => {
					resetAnimation();
				}, 500);
			},
		});
	};

	const getCanvasContext = () => {
		if(refs.current['paint_layer_ref'] == undefined) return false;
		return refs.current['paint_layer_ref'].getCanvas().getContext();
	};

	const progressStroke = () => {
		animateHand();
		stroke_anim = refs.current['scratch_stroke'].to({
			duration:5,
			dashOffset:0,
			onFinish:() => {
				setTimeout(() => {
					if(hand_move_anim != null && hand_move_anim.isRunning()) hand_move_anim.stop();
					hand_move_anim = null;
					moveHandOut();
				}, 100);
			},
		});
	};

	const animateHand = () => {
		hand_move_anim = new Konva.Animation((frame) => {
			const actual_offset = refs.current['scratch_stroke'].dashOffset();
			const actual_point = stroke_length.current - actual_offset;
			const pt = refs.current['scratch_stroke'].getPointAtLength(actual_point);
			refs.current['hand_ref'].position({ x:pt.x, y:pt.y });
			refs.current['pointer_layer'].draw();
		}, refs.current['paint_layer_ref']);
	
		hand_move_anim.start();
	}

	const adaptToContainer = () => {
		if(throttle_resize != undefined) clearTimeout(throttle_resize);

		throttle_resize = setTimeout(() => {
			killAllFrameAnimations();
			calculateAvailableHeight(true);
		}, 150);
	};

	const resetAnimation = () => {
		refs.current['scratch_stroke'].dashOffset(stroke_length.current);
		refs.current['cover_ref'].opacity(0).to({
			opacity:1,
			duration:0.6,
			onFinish:() => {
				setTimeout(() => {
					refs.current['paint_layer_ref'].draw();
					refs.current['cover_ref'].opacity(0);
				}, 1);
			}
		});
		setTimeout(() => {
			moveHandIn();
		}, 500);
	};

	useEffect(() => {
		calculateAvailableHeight();
		window.addEventListener('resize', adaptToContainer);

		if(refs.current['prize_icon'] != undefined) {
			refs.current['prize_icon'].cache(default_cache_props);
		}

		if(refs.current['hand_ref'] != undefined) {
			refs.current['hand_ref'].cache(default_cache_props);
		}
		
		if(refs.current['scratch_stroke'] != undefined) {
			stroke_length.current = refs.current['scratch_stroke'].getLength() + 10;
	
			const ctx = getCanvasContext();
			ctx.globalCompositeOperation = 'xor';
	
			refs.current['scratch_stroke'].setAttrs({
				dash:[stroke_length.current, stroke_length.current],
				dashOffset:stroke_length.current,
			});

			moveHandIn();
		}

		return () => {
			window.removeEventListener('resize', adaptToContainer);
			killAllFrameAnimations();
			refs.current['whole_instructions_ref'].destroyChildren();
		}
	}, []);

	const line_props = {
		stroke:'#000000',
		strokeWidth:1,
		lineCap:'round',
		lineJoin:'round',
	};

	const main_bg_color = '#FFFFFF';
	const main_color = props?.main_color || '#2951CC';

	const real_w = 150;
	const real_h = 150;
	const canvas_scale = Math.min(canvas_size_w, canvas_size_h) / Math.min(real_w, real_h);

	const normal_stroke_width = 2;
	const thin_stroke_width = Math.round(normal_stroke_width / 2);
	
	return (
		<div style={{width:canvas_size_w + 'px', height:canvas_size_h + 'px'}}>
			<Stage ref={(node) => { refs.current['whole_instructions_ref'] = node; }} width={canvas_size_w} height={canvas_size_h}>
				<Layer ref={(node) => { refs.current['prize_icon'] = node; }} scaleX={canvas_scale} scaleY={canvas_scale}>
					<Rect width={real_w} height={real_h} fill="gray" />

					<Group class="present" x={10 + real_w * 0.1} y={10 + real_w * 0.1} scaleX={0.8} scaleY={0.8}>
						<Group class="big_box">
							<Rect x={21.5} y={33} width={90} height={30} fill={main_color} {...line_props} />
							<Line points={[93.74,63,111.5,45.24,111.5,33,102.49,33,72.49,63]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[62.44,63,92.44,33,71.19,33,41.19,63]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[111.5,55.29,103.79,63,111.5,63]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[21.5,41.34,29.84,33,21.5,33]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[59,33,39.88,33,21.5,51.38,21.5,63,31.14,63,59,35.14]} closed={true} fill={main_bg_color} {...line_props} />
							<Rect x={59} y={33} width={15} height={30} fill={main_bg_color} {...line_props} />
							<Path data="M115,33H18V23c0-1.1,0.9-2,2-2h93c1.1,0,2,0.9,2,2V33z" fill={main_bg_color} {...line_props} />
						</Group>

						<Group class="small_box">
							<Rect x={4} y={75} width={112} height={45} fill={main_color} {...line_props} />
							<Line points={[54.63,75,9.63,120,32.17,120,77.17,75]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[65.37,120,110.37,75,87.83,75,42.83,120]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[116,113.23,109.23,120,116,120]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[76.03,120,98.57,120,116,102.57,116,80.03]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[4,81.77,10.77,75,4,75]} closed={true} fill={main_bg_color} {...line_props} />
							<Line points={[4,92.43,4,114.97,43.97,75,21.43,75]} closed={true} fill={main_bg_color} {...line_props} />
							<Rect x={50} y={75} width={20} height={45} fill={main_bg_color} {...line_props} />
							<Path data="M120,75H0V65c0-1.1,0.9-2,2-2h116c1.1,0,2,0.9,2,2V75z" fill={main_bg_color} {...line_props} />
						</Group>

						<Group class="lace">
							<Path data="M66.19,22C72.97,27.21,83.25,33.77,86,47c-6.89-7.08-13.62-9.58-13.54,0C65.13,39.92,62.27,28.67,61,22H66.19z" fill={main_bg_color} {...line_props} />
							<Path data="M63.31,10.47c3.5,0,14.88-6.7,18.7-9.32S93.92,0,91.58,9.63s-2.81,12.67-2.81,16.41c0,6-6.74,7.29-11.85,4.67c-5.1-2.62-12.67-2.97-14.16-6.94C60.76,18.4,63.31,10.47,63.31,10.47z" fill={main_bg_color} {...line_props} />
							<Path data="M59.81,22C53.03,27.21,42.75,33.77,40,47c6.89-7.08,13.62-9.58,13.54,0C60.87,39.92,63.73,28.67,65,22H59.81z" fill={main_bg_color} {...line_props} />
							<Path data="M62.69,10.47c-3.5,0-14.88-6.7-18.7-9.32S32.08,0,34.42,9.63s2.81,12.67,2.81,16.41c0,6,6.74,7.29,11.85,4.67s12.67-2.97,14.16-6.94C65.24,18.4,62.69,10.47,62.69,10.47z" fill={main_bg_color} {...line_props} />
							<Path data="M82,13c-4.14,4.64-11.53,6.67-17,4.31c5.2,5.26,9.69,6.2,14.28,6.69" {...line_props} />
							<Path data="M44,13c4.14,4.64,11.53,6.67,17,4.31c-5.2,5.26-9.69,6.2-14.28,6.69" {...line_props} />
							<Circle x={63} y={19} radius={9} fill={main_bg_color} {...line_props} />
						</Group>
					</Group>
				</Layer>

				<Layer ref={(node) => { refs.current['paint_layer_ref'] = node; }} scaleX={canvas_scale} scaleY={canvas_scale}>
					<Rect width={real_w} height={real_h} fill="lightgray" />

					<Path
						ref={(node) => { refs.current['scratch_stroke'] = node; }}
						data="M11.31,20.34c0-4.95,14.34-13.1,17.71-9.42C33.85,16.2,7.79,29.13,12.7,34.32c7.01,7.39,28.68-28.07,35.46-20.47C59.38,26.44-0.37,54.08,11.59,66C27.03,81.38,66.14,3.45,76.25,14.31C94.51,33.92,3.18,87.42,13.12,99.45c17.49,21.2,78.48-97.38,92.09-83.54c15.91,16.18-95.69,93.09-87.56,109.12C30.3,150,110.5,12.45,123.59,25.17C140,41.12,15.57,130.11,39.43,136.45c13.38,3.55,70.42-93.06,90.17-84.19c16.97,7.63-78.4,72.22-61.97,86.87c11.52,10.27,53.55-73.75,70.65-58.49c12.93,11.53-51.56,44.01-39.61,56.52c6.22,6.51,24-23.88,31.17-18.4c3.08,2.35-7.41,12.14-4.49,14.68c1.49,1.3,5.45,2.29,7.28-3.13"
						stroke={'#000000'}
						strokeWidth={14}
						lineCap={'round'}
						lineJoin={'round'}
					/>
				</Layer>

				<Layer ref={(node) => { refs.current['pointer_layer'] = node; }} scaleX={canvas_scale} scaleY={canvas_scale}>
					<Rect ref={(node) => { refs.current['cover_ref'] = node; }} width={real_w} height={real_h} fill="lightgray" opacity={0} />

					<Group ref={(node) => { refs.current['hand_ref'] = node; }}>
						<InstructionsHand
							scale={0.7}
							color={elements_color}
							bg_color={background_color}
							normal_stroke_width={normal_stroke_width}
							thin_stroke_width={thin_stroke_width}
						/>
					</Group>
				</Layer>
			</Stage>
		</div>
	)
}

export default ScratchInstructionsCanvas;